import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Upload, FileText, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { FORMS } from "@/lib/content";
import { Overline } from "@/components/Reveal";

const UPLOAD_FORMS = ["property_owner", "capital_request"];

export default function LeadForm({ formKey, sourcePage = "", onDone }) {
  const schema = FORMS[formKey];
  const [base, setBase] = useState({ name: "", email: "", phone: "" });
  const [extra, setExtra] = useState({});
  const [notes, setNotes] = useState("");
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!schema) return null;

  const allowUpload = UPLOAD_FORMS.includes(formKey);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setDocs((d) => [...d, { file_id: data.file_id, filename: data.filename }]);
      }
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", {
        name: base.name,
        email: base.email,
        phone: base.phone,
        lead_type: schema.lead_type,
        source_page: sourcePage,
        notes,
        data: { ...extra, ...(docs.length ? { documents: docs } : {}) },
      });
      setDone(true);
      toast.success("Submitted. The Moaglee team will be in touch.");
      if (onDone) onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-12 px-6 border border-white/10 bg-[#141414]" data-testid="form-success">
        <CheckCircle2 className="text-[#CC5500] mx-auto mb-4" size={42} />
        <h3 className="font-display text-2xl font-bold">Submission Received</h3>
        <p className="text-neutral-400 mt-2 max-w-md mx-auto">
          Thank you. Your information is now in the Moaglee command center. A strategist will review and follow up.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6" data-testid={`lead-form-${formKey}`}>
      <Overline>{schema.title}</Overline>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Name *" required value={base.name} onChange={(v) => setBase({ ...base, name: v })} testid="field-name" />
        <Field label="Email *" type="email" required value={base.email} onChange={(v) => setBase({ ...base, email: v })} testid="field-email" />
        <Field label="Phone" value={base.phone} onChange={(v) => setBase({ ...base, phone: v })} testid="field-phone" />
        {schema.fields.map((f) =>
          f.type === "select" ? (
            <div key={f.name}>
              <label className="overline text-neutral-400 mb-2 block">{f.label}</label>
              <select
                className="field-input"
                value={extra[f.name] || ""}
                onChange={(e) => setExtra({ ...extra, [f.name]: e.target.value })}
                data-testid={`field-${f.name}`}
              >
                <option value="">Select…</option>
                {f.options.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
          ) : (
            <Field
              key={f.name}
              label={f.label}
              value={extra[f.name] || ""}
              onChange={(v) => setExtra({ ...extra, [f.name]: v })}
              testid={`field-${f.name}`}
            />
          )
        )}
      </div>
      <div>
        <label className="overline text-neutral-400 mb-2 block">Notes</label>
        <textarea
          className="field-input resize-none"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          data-testid="field-notes"
        />
      </div>

      {allowUpload && (
        <div data-testid="documents-uploader">
          <label className="overline text-neutral-400 mb-2 block">Documents (optional)</label>
          <label className="flex items-center gap-3 border border-dashed border-white/20 hover:border-[#CC5500] transition-colors cursor-pointer p-4 text-sm text-neutral-400">
            {uploading ? <Loader2 className="animate-spin text-[#CC5500]" size={18} /> : <Upload size={18} className="text-[#CC5500]" />}
            <span>{uploading ? "Uploading…" : "Upload rent rolls, P&L, photos, or deal docs (PDF, XLSX, images — max 15MB)"}</span>
            <input
              type="file"
              multiple
              className="hidden"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.xls,.doc,.docx,.txt"
              disabled={uploading}
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="file-input"
            />
          </label>
          {docs.length > 0 && (
            <ul className="mt-3 space-y-2">
              {docs.map((d, i) => (
                <li key={d.file_id} className="flex items-center justify-between bg-[#1A1A1A] border border-white/10 px-3 py-2 text-sm">
                  <span className="flex items-center gap-2 text-neutral-300 truncate"><FileText size={14} className="text-[#CC5500] shrink-0" /> {d.filename}</span>
                  <button type="button" onClick={() => setDocs((arr) => arr.filter((_, idx) => idx !== i))} className="text-neutral-500 hover:text-red-400 shrink-0" data-testid={`remove-doc-${i}`}>
                    <X size={15} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid="submit-lead-btn">
        {submitting ? <Loader2 className="animate-spin" size={18} /> : "Submit"}
      </button>
    </form>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="overline text-neutral-400 mb-2 block">{label}</label>
      <input
        className="field-input"
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}
